import { useEffect, useState } from 'react'
import mixpanel from 'mixpanel-browser'
import * as Sentry from '@sentry/react'

import { isEmpty } from 'lodash'

import { RegistrationContext } from 'contexts'

import { useJourney } from 'hooks'
import { useLocation } from 'react-router-dom'

import { CompanyType } from 'types/company.types'
import { IntegrationPartnerType } from 'types/integrationPartner.types'
import { NodeChildren } from 'types/global.types'
import { SegmentType } from 'types/segment.types'

import queryString from 'query-string'
import humps from 'humps'

import { getMixPanelAuthKey } from 'service/env'
import service from 'service'

const RegistrationProvider = ({ children }: NodeChildren) => {
  const location = useLocation()
  const search = humps.camelizeKeys(
    queryString.parse(location?.search)
  ) as queryString.ParsedQuery
  const integrationToken = search?.partnerToken

  const [recaptcha, setRecaptcha] = useState<string | undefined | null>(null)
  const [integrationPartner, setIntegrationPartner] =
    useState<IntegrationPartnerType>()

  const bannerToken = service.dpoeasy.auth.getCompanyBannerToken()
  const user = {}
  const [companyChanged, setCompanyChanged] = useState(false)
  const [companyToken, setCompanyToken] = useState(
    service.dponet.auth.getRegistrationCompanyToken()
  )
  const [loaded, setLoaded] = useState(!companyToken)
  const [anotherSegment, setAnotherSegment] = useState<SegmentType>()
  const [bannerCreated, setBannerCreated] = useState(!!bannerToken)

  const { setActiveStep } = useJourney()

  const [company, setCompany] = useState<CompanyType>({})

  const updateCompany = (data: CompanyType) => {
    setCompany((company) => ({ ...company, ...data }))
  }

  const loadData = async (toActiveStep = 2) => {
    const token =
      service.dponet.auth.getRegistrationCompanyToken() || companyToken

    if (token) {
      try {
        setLoaded(false)
        const response = await service.dponet.company.getByToken(token)
        const companyResponse = response?.data?.company
        const bannerTokenResponse = companyResponse?.bannerToken
        const companyToken = companyResponse?.token
        if (!service.dpoeasy.auth.getRegistrationCookieToken()) {
          await service.dpoeasy.auth.registrationCookieToken({
            token: companyToken,
          })
        }
        mixpanelBrowser()

        updateCompany(companyResponse ?? company)
        Sentry.setUser(companyResponse ?? company)
        setCompanyToken(companyToken)
        if (bannerTokenResponse) {
          service.dpoeasy.auth.setCompanyBannerToken(bannerTokenResponse)
        }
        setBannerCreated(!!bannerTokenResponse)
        setActiveStep(toActiveStep)
      } catch {
        loadOut()
      } finally {
        setLoaded(true)
      }
    }
  }

  useEffect(() => {
    const loadIntegrationPartner = async () => {
      if (!isEmpty(integrationToken)) {
        const response = await service.dponet.integrationPartner.consult(
          integrationToken as string
        )

        setIntegrationPartner(response?.data?.integrationPartner)
      }
    }

    loadIntegrationPartner()
  }, [integrationToken])

  const loadOut = () => {
    setCompany({})
    Sentry.setUser(null)
    service.dponet.auth.removeRegistrationCompanyToken()
    service.dpoeasy.auth.removeRegistrationCookieToken()
    service.dpoeasy.auth.removeCompanyBannerToken()
  }

  const createCompany = async () => {
    if (!company?.token) {
      const response = await service.dponet.company.create(company)
      const companyToken = response?.data?.company?.token
      service.dponet.auth.setRegistrationCompanyToken(companyToken)
      setCompanyToken(companyToken)
      return updateCompany(response?.data?.company ?? {})
    }

    if (companyChanged) {
      await service.dponet.company.update(company?.token, company)
      setCompanyChanged(false)
    }
  }

  const createBanner = async () => {
    if (!bannerToken) {
      if (!service.dpoeasy.auth.getRegistrationCookieToken()) {
        const token =
          company?.token || service.dponet.auth.getRegistrationCompanyToken()
        await service.dpoeasy.auth.registrationCookieToken({
          token,
        })
      }

      const response = await service.dpoeasy.banner.create({
        name: `Banner de cookies da ${company?.name}`,
        status: 'unconfigured',
        urls: [{ url: company?.siteUrl }],
      })
      if (response?.data?.token) {
        service.dpoeasy.auth.setCompanyBannerToken(response?.data?.token)
      }
    } else {
      if (companyChanged && bannerToken) {
        if (!service.dpoeasy.auth.getRegistrationCookieToken()) {
          await service.dpoeasy.auth.registrationCookieToken({
            bannerToken,
          })
        }
        await service.dpoeasy.banner.updateByToken({
          token: bannerToken,
          banner: {
            name: `Banner de cookies da ${company?.name}`,
            urls: [{ url: company?.siteUrl }],
          },
        })
      }
    }
  }

  const mixpanelBrowser = () => {
    mixpanel.init(getMixPanelAuthKey(), {
      debug: false, // TRUE somente em um ambiente de desenvolvimento
      ignore_dnt: true,
      secure_cookie: true,
      cross_site_cookie: false,
      api_transport: 'XHR',
      persistence: 'cookie',
      batch_requests: false,
    })

    if (companyToken) {
      mixpanel.register_once({
        companyToken,
      })
    }
  }

  return (
    <RegistrationContext.Provider
      value={{
        anotherSegment,
        bannerCreated,
        company,
        companyChanged,
        createBanner,
        createCompany,
        loadData,
        loaded,
        loadOut,
        recaptcha,
        integrationPartner,
        setAnotherSegment,
        setBannerCreated,
        setCompanyChanged,
        setCompanyToken,
        updateCompany,
        setRecaptcha,
        user,
        mixpanelBrowser,
      }}
    >
      {children}
    </RegistrationContext.Provider>
  )
}

export default RegistrationProvider
